import PropTypes from 'prop-types';
import cx from 'classnames';
import { useRef } from 'react';
import { Alert } from 'antd';

import { ModalCustom } from '$src/components/Modal';
import useStores from '$src/hooks/useStores';
import useModalEvents from '$src/hooks/useModalEvents';
import { ONBOARDING_BACK, ONBOARDING_CLOSE, ONBOARDING_PRIMARY, ONBOARDING_SECONDARY } from '$src/constants/actions';

import { Loader } from '@amzn/ring-react-components/csr/components/Loader';
import { Button } from '@amzn/ring-react-components/csr/components/Button';
import NavigationTitle from '../NavigationTitle';
import Header from '../Steps/Header';

import './index.less';

function OnboardingContainer({
  icon,
  subtitle,
  title,
  className,
  headerTitle,
  handleClose,
  handleBack,
  children,
  image,
  primaryText,
  primaryActionHandler,
  secondaryText,
  secondaryActionHandler,
  alert,
  loading,
  warningOnClose,
  warningOnBack,
  hideBack,
  footer,
  onCloseAlert,
  ...props
}) {
  const {
    statsStore: { sendStats },
  } = useStores();
  const [handleModalClick, showModal] = useModalEvents();
  const callbackCancel = useRef();

  const decorateHandlerWithStats =
    ({ callback, action }) =>
    () => {
      sendStats(action);
      callback();
    };

  const handlerCallback = (warningEnabled, callback) => {
    if (warningEnabled) {
      callbackCancel.current = callback;
      handleModalClick();
    } else {
      callback();
    }
  };

  return (
    <div className={cx('onboarding-container', className)} data-testid={props['data-testid']}>
      <div className="onboarding-container__header">
        <Header
          handleClose={() => handlerCallback(warningOnClose, decorateHandlerWithStats({ callback: handleClose, action: ONBOARDING_CLOSE }))}
          handleBack={() => handlerCallback(warningOnBack, decorateHandlerWithStats({ callback: handleBack, action: ONBOARDING_BACK }))}
          hideBack={hideBack}
        >
          {headerTitle}
        </Header>
      </div>
      <div className="onboarding-container__wrapper">
        {image && (
          <div className="onboarding-container__wrapper__image-container">
            <img src={image} className="onboarding-container__wrapper__image-panel" />
          </div>
        )}
        <div className="onboarding-container__wrapper__content">
          <div className={cx('onboarding-container__wrapper__content__header', { 'alert-margin': !!alert })}>
            {alert && (
              <Alert
                message={alert.message}
                banner
                type={alert.type}
                showIcon={false}
                closable
                afterClose={onCloseAlert}
                className="onboarding-container__wrapper__content__header--alert"
              />
            )}
            <NavigationTitle icon={icon} title={title}>
              {subtitle}
            </NavigationTitle>
          </div>
          <div className="onboarding-container__wrapper__content__body">{children}</div>
        </div>
      </div>
      <div className="onboarding-container__footer">
        {footer ? (
          <>{footer}</>
        ) : (
          <>
            <Button
              color="primary"
              size="m"
              variant="filled"
              data-testid="onboarding-primary-action"
              onClick={decorateHandlerWithStats({ callback: primaryActionHandler, action: ONBOARDING_PRIMARY })}
              disabled={loading}
            >
              {loading ? <Loader className="onboarding-container__footer-loader" /> : primaryText}
            </Button>

            {secondaryText && (
              <Button
                className="ring-button"
                color="primary"
                size="m"
                variant="outline"
                data-testid="onboarding-secondary-action"
                disabled={loading}
                onClick={decorateHandlerWithStats({ callback: secondaryActionHandler, action: ONBOARDING_SECONDARY })}
              >
                {secondaryText}
              </Button>
            )}
          </>
        )}
      </div>
      <ModalCustom
        title="Setup Incomplete"
        contentTitle="Your Virtual Security Guard setup is not complete."
        icon="warning_blue.svg"
        width="650px"
        contentMessage="The service will not operate until you have a device enrolled and Professional Monitoring is enabled. Are you sure you want to exit setup?"
        visible={showModal}
        handleCancel={callbackCancel.current}
        handleOk={handleModalClick}
        setModalVisible={handleModalClick}
        okText="Continue Setup"
        cancelText="Exit"
        primaryActionOnCross
        data-testid="setup-incomplete-warning"
      />
    </div>
  );
}

OnboardingContainer.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string.isRequired,
  headerTitle: PropTypes.string.isRequired,
  handleClose: PropTypes.func,
  handleBack: PropTypes.func,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  footer: PropTypes.node,
  icon: PropTypes.func,
  image: PropTypes.string,
  primaryText: PropTypes.string,
  primaryActionHandler: PropTypes.func.isRequired,
  secondaryText: PropTypes.string,
  secondaryActionHandler: PropTypes.func,
  alert: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  loading: PropTypes.bool,
  className: PropTypes.string,
  warningOnClose: PropTypes.bool,
  warningOnBack: PropTypes.bool,
  hideBack: PropTypes.bool,
  onCloseAlert: PropTypes.func,
  'data-testid': PropTypes.string,
};

export default OnboardingContainer;
